import * as React from 'react';
import styled from 'styled-components';
import { TbClipboardText } from 'react-icons/tb';
import commonStore from '../../store/common';
import ReportCellComment from '../../pages/Report/components/ReportComments/ReportCellComment';
import { Text, Tooltip } from '@mantine/core';
import { getCellState, isCellEmpty } from '../../utils/cellTemplate';

const isAlphaNumericKey = (keyCode: number) => {
  return (
    (keyCode >= 48 && keyCode <= 57) || // 0-9
    (keyCode >= 65 && keyCode <= 90) || // A-Z
    (keyCode >= 97 && keyCode <= 122) // a-z
  );
};

const isNavigationKey = (keyCode: number) => {
  return (
    keyCode === 37 || // left
    keyCode === 38 || // up
    keyCode === 39 || // right
    keyCode === 40 || // down
    keyCode === 13 || // enter
    keyCode === 9 // tab
  );
};

const isArrowKey = (keyCode: number) => keyCode >= 37 && keyCode <= 40; // left, up, right, down

const keyCodes = {
  POINTER: 32, // space
  ENTER: 13
};

const jurisdictionFields = ['last_reporting_flag'];

// Lazy-mounts the comment icon/popover after the first hover (see SelectCellView
// in NewSelectCellTemplate for the rationale). Cells with comments still render
// the icon regardless, handled inside ReportCellComment.
const BooleanCellView = ({
  cell,
  onCellChanged
}: {
  cell: any;
  onCellChanged: (cell: any, commit: boolean) => void;
}) => {
  const [commentMounted, setCommentMounted] = React.useState(false);
  const isEmptyValue = isCellEmpty(cell.selectedValue);
  const { background } = getCellState(cell, isEmptyValue);
  const isJurisdictionField = jurisdictionFields.includes(cell.dataKey);

  const view = (
    <SelectContainer
      hasClipBoardIcon={cell.isStaticField}
      style={{
        background
      }}
      className="report-field"
      onPointerEnter={() => setCommentMounted(true)}
    >
      <div
        className="select"
        style={{
          height: cell.errorMessage || cell.required ? 'auto' : '100%'
        }}
      >
        <SelectStyle
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            color: isEmptyValue ? '#ADB5BD' : '#000',
            background: 'transparent',
            fontSize: '12px',
            padding: 0,
            height: cell.errorMessage || cell.required ? 'auto' : '100%'
          }}
          onChange={e => {
            onCellChanged(
              {
                ...cell,
                selectedValue: e.currentTarget.value
              },
              true
            );
          }}
          disabled={
            (!cell.isStaticTemplate && cell.isStaticField) ||
            commonStore.isViewer ||
            cell.nonEditable
          }
          onCopy={e => e.stopPropagation()}
          onCut={e => e.stopPropagation()}
          onPaste={e => e.stopPropagation()}
          // Must stop propagation: if the pointerdown reaches ReactGrid it starts
          // its own pointer/selection handling and re-renders on the same event,
          // which kills the in-progress native dropdown.
          onPointerDown={e => e.stopPropagation()}
          onKeyDown={e => {
            // Delete/Backspace: a clicked <select> keeps DOM focus (its pointerdown
            // is stopped so the dropdown can open), so ReactGrid's own clear would
            // target its stale focused cell instead of this one. Clear this cell
            // directly and stop propagation so the grid doesn't clear the wrong one.
            if (e.keyCode === 46 || e.keyCode === 8) {
              e.stopPropagation();
              onCellChanged({ ...cell, selectedValue: null }, true);
              return;
            }
            // Arrow keys must reach ReactGrid (registered as onKeyDown on an
            // ancestor container) so focus can move between cells. Block only the
            // native <select>'s own arrow handling — which would silently change
            // the value while closed — but let the event propagate to navigate.
            if (isArrowKey(e.keyCode)) {
              e.preventDefault();
              return;
            }
            if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) e.stopPropagation();
          }}
          value={isEmptyValue ? '' : cell.selectedValue ? 'Yes' : 'No'}
        >
          <option value="">{commonStore.isViewer ? '' : 'Select an option'}</option>
          {[
            {
              label: 'Yes',
              value: 'Yes'
            },
            {
              label: 'No',
              value: 'No'
            }
          ].map(item => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </SelectStyle>
        {cell?.difference?.is_changed && (
          <Text fz="12px" c="orange.6" style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>
            Changed
          </Text>
        )}
        {cell.isStaticField && <TbClipboardText fontSize="12px" />}
      </div>
      {cell.errorMessage ? (
        <p className="error-message">{cell.errorMessage}</p>
      ) : cell.required && isEmptyValue ? (
        <p className="error-message">This field is mandatory</p>
      ) : (
        ''
      )}
      <ReportCellComment
        fundId={cell.fundId}
        fieldName={cell.dataKey}
        fundName={cell.fundName}
        tableName={cell.tableName}
        fieldOrder={cell.fieldOrder}
        cellHovered={commentMounted}
      />
    </SelectContainer>
  );

  if (!isJurisdictionField) return view;

  return (
    <Tooltip
      label="This field is populated from the Jurisdictions section and is editable exclusively from that page."
      maw="321px"
      multiline
      bg="gray.6"
      fz="12px"
      lh="18px"
      p="8px"
      zIndex={99999}
      hidden={!cell?.fundId}
    >
      {view}
    </Tooltip>
  );
};

export class NewBooleanCellTemplate {
  getCompatibleCell(uncertainCell: any) {
    return { ...uncertainCell };
  }

  handleKeyDown(cell: any, keyCode: number, ctrl: boolean, shift: boolean, alt: boolean) {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) return { cell, enableEditMode: true };
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  }

  update(cell: any, cellToMerge: any) {
    const isCleared = cellToMerge.type === '';
    return this.getCompatibleCell({
      ...cell,
      selectedValue: isCleared ? null : (cellToMerge.selectedValue ?? cellToMerge.text)
    });
  }

  render(cell: any, isInEditMode: boolean, onCellChanged: (cell: any, commit: boolean) => void) {
    return <BooleanCellView cell={cell} onCellChanged={onCellChanged} />;
  }
}

const SelectStyle = styled.select`
  border: 1px solid transparent;
  width: 100%;
  outline: none;
  &:disabled {
    color: #909296 !important;
  }
`;

const SelectContainer = styled.div<any>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 4px;
  justify-content: center;

  .cell-comment-container {
    right: ${({ hasClipBoardIcon }) => (hasClipBoardIcon ? 35 : 30)}px;
  }

  &:hover {
    .cell-comment-container {
      visibility: visible;
    }
  }

  .select {
    display: flex;
    align-items: center;
  }

  p {
    font-size: 12px;
    line-height: 17px;
    padding: 0 4px;
    color: #ff6b6b;
    white-space: initial;
  }
`;
